import {
  collectWorldTriangles,
  type WorldTrianglesSource,
} from '@viewer/lib/worldTriangles'
import { fitArcsAll } from './arcFit'
import { assembleLoops, LOOP_TOL } from './assembleLoops'
import { cutTriangles } from './cutTriangles'
import { featureEdges, FEATURE_ANGLE_DEG } from './featureEdges'
import {
  createDepthMap,
  occludeSegments,
  projectedExtent,
  rasterizeMesh,
  type DepthMap,
  type DepthMapOptions,
  type OccludeOptions,
} from './hiddenLines'
import {
  clipPolylineToRect,
  projectSegments,
  projectSegmentsClipped,
  projectSegmentsClippedW,
} from './projectToView'
import {
  DEFAULT_LAYER,
  DEFAULT_SCALE_DENOM,
  sub,
  viewBasis,
  viewPlane,
  type Drawing2D,
  type DrawingLayer,
  type Polyline2D,
  type Vec3,
  type View2DDef,
  type View2DStats,
  type ViewBasis,
} from './viewTypes'

// ---------------------------------------------------------------------------
// Orquestrador: modelo Fragments + definição de vista -> Drawing2D.
//
// Pipeline:
//  1. pré-filtro por caixa de MUNDO (getBoxes) contra a laje
//     [plano, plano+depth] e contra a janela (crop), se houver. Evita
//     descarregar geometria que não entra no desenho.
//  2. recolha de triângulos no mundo (collectWorldTriangles).
//  3. elementos ATRAVESSADOS pelo plano -> cutTriangles -> projecção ->
//     encadeamento -> camada `cut`.
//  4. elementos com massa dentro da profundidade (incluindo a parte dos
//     atravessados que fica para lá do plano) -> featureEdges ->
//     projecção aparada a 0 <= w <= depth -> encadeamento -> camada
//     `projection`.
//  5. agrupamento por categoria IFC em camadas, recorte à janela e
//     cálculo do bbox.
//
// PRECISÃO EM MODELOS GEORREFERENCIADOS (quarta leva): os triângulos
// são pedidos RELATIVOS a um ponto perto da geometria
// (`collectWorldTriangles({ origin })`) e o corte/projecção correm nesse
// referencial deslocado. Sem isto, um modelo com coordenadas na casa
// dos 1,37e6 m perdia ~0,125 m no Float32 das posições de mundo, e o
// desenho saía dentado e fora do sítio (círculos poligonais).
//
// A origem de recolha NÃO é a origem da vista: a `planViewDef` põe a
// origem da planta em (0, cota, 0), que num modelo georreferenciado
// está a 1,37e6 m da geometria e não resolvia nada em x/z. Usa-se o
// CENTRO da união das caixas dos elementos que ficaram no pré-filtro,
// que está por construção dentro do modelo. A origem da vista continua
// a mandar nas coordenadas do desenho: o referencial local guarda-a
// como `origin - C`, e a projecção `(p - C) - (origin - C)` dá
// exactamente os mesmos números de antes, só que calculados em float64
// do princípio ao fim.
//
// Remoção de linhas ocultas (HLR): OPCIONAL (`opts.hiddenLines`) e
// desligada por omissão, porque é a passagem mais cara da geração. Com
// ela desligada, a projecção desenha todas as arestas de feição dentro
// da profundidade, mesmo as tapadas por outro elemento: em plantas com
// profundidade curta lê-se bem, em cortes profundos fica "carregado".
// Com ela ligada, as arestas de projecção são testadas contra um mapa
// de profundidade (ver `hiddenLines.ts`). Documentado em
// docs/VISTAS2D.md.
// ---------------------------------------------------------------------------

interface BoxLike {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
}

export interface View2DModelSource extends WorldTrianglesSource {
  /** Caixas de MUNDO por elemento (Fragments 3.4.3 devolve mundo). */
  getBoxes?: (ids: number[]) => Promise<BoxLike[]>
}

export interface GenerateView2DOptions {
  /** Restringe a geração a estes elementos (senão usa o modelo todo). */
  ids?: number[]
  /** Categoria IFC do elemento, para a camada (ex.: 'IfcWall'). */
  categoryOf?: (localId: number) => string | null | undefined
  /** Cor da camada a partir da categoria (hex CSS). */
  colorForCategory?: (category: string) => string | null | undefined
  /** Limiar do ângulo diedro das arestas de feição, em graus. */
  featureAngleDeg?: number
  /** Tolerância de solda no encadeamento, em metros. */
  tol?: number
  maxTriangles?: number
  chunkSize?: number
  /** Desliga a projecção (só linhas de corte). Muito mais rápido. */
  includeProjection?: boolean
  /** Reconhece círculos e arcos na tesselação (ver `arcFit.ts`). LIGADO
   *  por omissão: sem isto uma laje redonda sai polígono de 16 lados. */
  fitArcs?: boolean
  /** Desenha o CONTORNO APARENTE das superfícies curvas (silhueta na
   *  direcção da vista). LIGADO por omissão: sem isto um tubo em planta
   *  sai sem as duas linhas paralelas do diâmetro (ver
   *  `featureEdges.ts`). */
  silhouette?: boolean
  /** Esconde as linhas de projecção tapadas por geometria à frente
   *  (HLR). DESLIGADO por omissão: é a passagem mais cara da geração e
   *  o painel liga-a explicitamente. Ver `hiddenLines.ts`. */
  hiddenLines?: boolean
  /** Afinações do mapa de profundidade do HLR. */
  hiddenLineOptions?: DepthMapOptions & OccludeOptions
}

/** Folga usada nas comparações de caixa, em metros. */
const BOX_EPS = 1e-6

/** Gera o desenho 2D de uma vista. Nunca lança por causa dos dados do
 *  modelo: APIs em falta ou geometria ilegível dão um desenho vazio ou
 *  incompleto, com as contagens em `stats`. */
export async function generateView2D(
  model: View2DModelSource,
  view: View2DDef,
  opts?: GenerateView2DOptions,
): Promise<Drawing2D> {
  const basis = viewBasis(view)
  const tol = opts?.tol ?? LOOP_TOL
  const angle = opts?.featureAngleDeg ?? FEATURE_ANGLE_DEG
  const wantProjection = opts?.includeProjection !== false
  // Direcção do contorno aparente. É a direcção de observação da vista,
  // que é a mesma no referencial do mundo e no da recolha (uma direcção
  // não se desloca).
  const silhouetteDir: Vec3 | null =
    opts?.silhouette === false ? null : basis.viewDir
  // O reconhecimento corre DEPOIS do recorte à janela: o recorte parte a
  // polilinha em troços novos e invalidaria os índices dos arcos.
  const arcs = (polys: Polyline2D[]): Polyline2D[] =>
    opts?.fitArcs === false ? polys : fitArcsAll(polys)
  const scaleDenom = view.scaleDenom ?? DEFAULT_SCALE_DENOM
  const stats: View2DStats = {
    candidates: 0,
    cutItems: 0,
    projectedItems: 0,
    skipped: 0,
    truncated: false,
    triangles: 0,
  }

  let ids: number[]
  if (opts?.ids) {
    ids = opts.ids
  } else {
    try {
      ids = await model.getItemsIdsWithGeometry()
    } catch {
      ids = []
    }
  }
  if (!ids || ids.length === 0) {
    return emptyDrawing(view, scaleDenom, stats)
  }

  // --- 1. pré-filtro por caixa ---------------------------------------------
  let boxes: BoxLike[] | null = null
  if (model.getBoxes) {
    try {
      boxes = await model.getBoxes(ids)
    } catch {
      boxes = null
    }
  }
  const straddling = new Set<number>()
  let kept: number[] = ids
  // União das caixas dos elementos mantidos: dá o ponto de referência da
  // recolha (ver nota de precisão no topo).
  const acc = { min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] }
  if (boxes && boxes.length === ids.length) {
    kept = []
    for (let i = 0; i < ids.length; i++) {
      const b = boxes[i]
      const r = boxRange(b, basis)
      if (!r) continue
      if (r.wMax < -BOX_EPS) continue // aquém do plano de corte
      if (r.wMin > basis.depth + BOX_EPS) continue // para lá da profundidade
      if (view.crop && !overlapsCrop(r, view.crop)) continue
      kept.push(ids[i])
      if (r.wMin <= BOX_EPS && r.wMax >= -BOX_EPS) straddling.add(ids[i])
      for (let k = 0; k < 3; k++) {
        const lo = k === 0 ? b.min.x : k === 1 ? b.min.y : b.min.z
        const hi = k === 0 ? b.max.x : k === 1 ? b.max.y : b.max.z
        if (lo < acc.min[k]) acc.min[k] = lo
        if (hi > acc.max[k]) acc.max[k] = hi
      }
    }
  } else {
    // Sem caixas: assume-se que todos podem ser cortados.
    for (const id of ids) straddling.add(id)
  }
  stats.candidates = kept.length
  if (kept.length === 0) {
    return emptyDrawing(view, scaleDenom, stats)
  }

  // Origem da recolha: centro da união das caixas mantidas. Sem caixas
  // fica a origem da vista, que é o melhor palpite disponível.
  const collectOrigin: Vec3 = Number.isFinite(acc.min[0])
    ? [
        (acc.min[0] + acc.max[0]) / 2,
        (acc.min[1] + acc.max[1]) / 2,
        (acc.min[2] + acc.max[2]) / 2,
      ]
    : [...basis.origin]
  // Referencial da vista expresso no frame da recolha: mesmos eixos, e a
  // origem da vista deslocada de `collectOrigin`. Projectar aqui dá
  // exactamente as mesmas coordenadas de desenho que projectar no mundo.
  const localBasis: ViewBasis = {
    ...basis,
    origin: sub(basis.origin, collectOrigin),
  }
  const plane = viewPlane(localBasis)

  // --- 2. recolha de triângulos --------------------------------------------
  const collected = await collectWorldTriangles(model, {
    ids: kept,
    maxTriangles: opts?.maxTriangles,
    chunkSize: opts?.chunkSize,
    origin: collectOrigin,
  })
  stats.skipped = collected.skipped
  stats.truncated = collected.truncated
  stats.triangles = collected.triangles

  // --- 3b. mapa de profundidade (HLR) --------------------------------------
  // Uma passagem só sobre os triângulos JÁ recolhidos, antes de tratar
  // as arestas: o mapa tem de estar completo quando a primeira aresta
  // for testada, senão um elemento só era tapado pelos que viessem
  // antes dele.
  let depthMap: DepthMap | null = null
  if (opts?.hiddenLines && collected.items.length > 0) {
    const t0 = now()
    const extent = Number.isFinite(acc.min[0])
      ? projectedExtent(
          {
            min: sub([acc.min[0], acc.min[1], acc.min[2]], collectOrigin),
            max: sub([acc.max[0], acc.max[1], acc.max[2]], collectOrigin),
          },
          localBasis,
        )
      : boxlessExtent(collected.items, localBasis)
    depthMap = createDepthMap(extent, opts.hiddenLineOptions)
    if (depthMap) {
      for (const item of collected.items) {
        rasterizeMesh(
          depthMap,
          item.positions,
          item.indices,
          localBasis,
          opts.hiddenLineOptions,
        )
      }
      stats.hiddenLines = {
        cells: depthMap.nx * depthMap.ny,
        cellM: depthMap.cell,
        ms: Math.round(now() - t0),
      }
    }
  }

  // --- 3/4. corte e projecção por elemento ---------------------------------
  const byLayer = new Map<string, DrawingLayer>()
  const layerOf = (localId: number): DrawingLayer => {
    let cat: string | null | undefined
    try {
      cat = opts?.categoryOf?.(localId)
    } catch {
      cat = null
    }
    const name = layerName(cat)
    let layer = byLayer.get(name)
    if (!layer) {
      let colorHex: string | undefined
      try {
        colorHex = (cat ? opts?.colorForCategory?.(cat) : undefined) ?? undefined
      } catch {
        colorHex = undefined
      }
      layer = { name, cut: [], projection: [], colorHex }
      byLayer.set(name, layer)
    }
    return layer
  }

  for (const item of collected.items) {
    const layer = layerOf(item.localId)

    if (straddling.has(item.localId)) {
      const segs3 = cutTriangles(item.positions, item.indices, plane)
      if (segs3.length > 0) {
        const segs2 = projectSegments(segs3, localBasis)
        const asm = assembleLoops(segs2, tol)
        const polys = [...asm.loops, ...asm.open]
        const cropped = arcs(tag(applyCrop(polys, view), item.localId))
        if (cropped.length > 0) {
          layer.cut.push(...cropped)
          stats.cutItems++
        }
      }
    }

    if (wantProjection) {
      const edges = featureEdges(
        item.positions,
        item.indices,
        angle,
        undefined,
        silhouetteDir,
      )
      if (edges.length > 0) {
        const segs2 = depthMap
          ? occludeSegments(
              depthMap,
              projectSegmentsClippedW(edges, localBasis),
              opts?.hiddenLineOptions,
            )
          : projectSegmentsClipped(edges, localBasis)
        if (segs2.length > 0) {
          const asm = assembleLoops(segs2, tol)
          const polys = [...asm.loops, ...asm.open]
          const cropped = arcs(tag(applyCrop(polys, view), item.localId))
          if (cropped.length > 0) {
            layer.projection.push(...cropped)
            stats.projectedItems++
          }
        }
      }
    }
  }

  const layers = [...byLayer.values()]
    .filter((l) => l.cut.length > 0 || l.projection.length > 0)
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))

  return {
    name: view.name,
    scaleDenom,
    unit: 'm',
    bbox: view.crop ? { ...view.crop } : bboxOfLayers(layers),
    layers,
    stats,
  }
}

/** Nome de camada a partir da categoria IFC: maiúsculas, sem espaços. */
export function layerName(category: string | null | undefined): string {
  const c = (category ?? '').trim()
  if (!c) return DEFAULT_LAYER
  return c.toUpperCase().replace(/\s+/g, '_')
}

/** Carimba o elemento de origem em cada polilinha. Faz-se DEPOIS do
 *  recorte à janela porque o `clipPolylineToRect` parte a polilinha em
 *  troços novos e não tem (nem deve ter) noção de elementos. */
function tag(polys: Polyline2D[], localId: number): Polyline2D[] {
  return polys.map((p) => (p.localId === localId ? p : { ...p, localId }))
}

function applyCrop(polys: Polyline2D[], view: View2DDef): Polyline2D[] {
  if (!view.crop) return polys
  const out: Polyline2D[] = []
  for (const p of polys) out.push(...clipPolylineToRect(p, view.crop))
  return out
}

/** Relógio monótono, com recurso ao Date quando não há performance
 *  (Node antigo, ambientes de teste minimalistas). */
function now(): number {
  return typeof performance !== 'undefined' &&
    typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

/** Extensão projectada quando o modelo não deu caixas: percorre os
 *  vértices recolhidos. Só se chega aqui em modelos sem `getBoxes`. */
function boxlessExtent(
  items: Array<{ positions: Float32Array | number[] }>,
  basis: ViewBasis,
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  const o = basis.origin
  for (const it of items) {
    const p = it.positions
    for (let i = 0; i + 2 < p.length; i += 3) {
      const dx = p[i] - o[0]
      const dy = p[i + 1] - o[1]
      const dz = p[i + 2] - o[2]
      const x =
        dx * basis.right[0] + dy * basis.right[1] + dz * basis.right[2]
      const y = dx * basis.up[0] + dy * basis.up[1] + dz * basis.up[2]
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 }
  return { minX, minY, maxX, maxY }
}

interface BoxRange {
  wMin: number
  wMax: number
  xMin: number
  xMax: number
  yMin: number
  yMax: number
}

/** Extensão da caixa nos eixos da vista (x, y e profundidade), pelos 8
 *  cantos. Null se a caixa for inválida ou vazia. */
function boxRange(b: BoxLike | undefined, basis: ViewBasis): BoxRange | null {
  if (!b || !b.min || !b.max) return null
  const { min, max } = b
  if (
    !Number.isFinite(min.x) ||
    !Number.isFinite(min.y) ||
    !Number.isFinite(min.z) ||
    !Number.isFinite(max.x) ||
    !Number.isFinite(max.y) ||
    !Number.isFinite(max.z)
  ) {
    return null
  }
  if (max.x < min.x || max.y < min.y || max.z < min.z) return null
  let wMin = Infinity
  let wMax = -Infinity
  let xMin = Infinity
  let xMax = -Infinity
  let yMin = Infinity
  let yMax = -Infinity
  const o = basis.origin
  for (let i = 0; i < 8; i++) {
    const p: Vec3 = [
      i & 1 ? max.x : min.x,
      i & 2 ? max.y : min.y,
      i & 4 ? max.z : min.z,
    ]
    const dx = p[0] - o[0]
    const dy = p[1] - o[1]
    const dz = p[2] - o[2]
    const w =
      dx * basis.viewDir[0] + dy * basis.viewDir[1] + dz * basis.viewDir[2]
    const x = dx * basis.right[0] + dy * basis.right[1] + dz * basis.right[2]
    const y = dx * basis.up[0] + dy * basis.up[1] + dz * basis.up[2]
    if (w < wMin) wMin = w
    if (w > wMax) wMax = w
    if (x < xMin) xMin = x
    if (x > xMax) xMax = x
    if (y < yMin) yMin = y
    if (y > yMax) yMax = y
  }
  return { wMin, wMax, xMin, xMax, yMin, yMax }
}

function overlapsCrop(
  r: BoxRange,
  crop: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  return (
    r.xMax >= crop.minX - BOX_EPS &&
    r.xMin <= crop.maxX + BOX_EPS &&
    r.yMax >= crop.minY - BOX_EPS &&
    r.yMin <= crop.maxY + BOX_EPS
  )
}

/** Caixa envolvente de todas as polilinhas do desenho. Desenho vazio dá
 *  uma caixa a zeros (os writers tratam-na como folha em branco). */
export function bboxOfLayers(layers: DrawingLayer[]): Drawing2D['bbox'] {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const l of layers) {
    for (const group of [l.cut, l.projection]) {
      for (const pl of group) {
        for (const p of pl.points) {
          if (p[0] < minX) minX = p[0]
          if (p[0] > maxX) maxX = p[0]
          if (p[1] < minY) minY = p[1]
          if (p[1] > maxY) maxY = p[1]
        }
      }
    }
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 }
  return { minX, minY, maxX, maxY }
}

function emptyDrawing(
  view: View2DDef,
  scaleDenom: number,
  stats: View2DStats,
): Drawing2D {
  return {
    name: view.name,
    scaleDenom,
    unit: 'm',
    bbox: view.crop
      ? { ...view.crop }
      : { minX: 0, minY: 0, maxX: 0, maxY: 0 },
    layers: [],
    stats,
  }
}
